import { useTranslations } from '@vocab/react';
import { Actions, Button, type useToast } from 'braid-design-system';
import { observer } from 'mobx-react';
import {
  type ComponentProps,
  type ReactElement,
  type ReactNode,
  useState,
} from 'react';

import { noop } from '../../type';
import { StickyButtonsFormView } from '../StickyButtonsFormView';

import translations from './.vocab';

import * as styles from './ActionableFormView.css';

export enum ACTION_RESULT {
  NONE,
  LOADING,
  SUCCESS,
  ERROR,
}

export type ErrorMessageType = ReactNode;

export type ToastAction = Parameters<ReturnType<typeof useToast>>[0]['action'];
export interface ActionResult {
  type: ACTION_RESULT.SUCCESS | ACTION_RESULT.ERROR;
  message: string;
  errorMessage: ErrorMessageType;
  description?: string;
  toastAction?: ToastAction;
}

export const okResult = (
  message: string,
  description?: string,
  action?: ToastAction,
): ActionResult => ({
  type: ACTION_RESULT.SUCCESS,
  message,
  errorMessage: '',
  description,
  toastAction: action,
});

export const failResult = (errorMessage: ErrorMessageType): ActionResult => ({
  type: ACTION_RESULT.ERROR,
  message: '',
  errorMessage,
});
export interface ActionableFormViewProps {
  handleSubmit?: any;
  onSubmit: (formValues?: any) => Promise<ActionResult>;
  onCancel?: () => void;
  resetForm?: () => void;
  submitButtonLabel: string;
  submitButtonLoadingLabel: string;
  children: ReactElement;
  onSubmitSuccess?: (
    message: string,
    description?: string,
    action?: ToastAction,
  ) => void;
  onSubmitError?: (message: ErrorMessageType) => void;
  /**
   * onSubmitErrorWithSetErrorMessage
   *
   * @description This function is called when error message is displayed if an error occurs.
   * This does not get called when onSubmitError is also provided.
   * FIXME: This is NOT ideal. We should only have one onSubmitError that uses the `setErrorMessage` function.
   */
  onSubmitErrorWithSetErrorMessage?: () => void;
  isStickyButtons?: boolean;
  footerElement?: ReactElement;
  buttonSize?: ComponentProps<typeof Button>['size'];
}

const defaultHandleSubmit = (callback: any) => (event: Event) => {
  event.preventDefault();
  callback();
};
export const ActionableFormView = ({
  handleSubmit = defaultHandleSubmit,
  onSubmit,
  submitButtonLabel,
  submitButtonLoadingLabel,
  onCancel,
  resetForm,
  onSubmitError,
  onSubmitErrorWithSetErrorMessage,
  onSubmitSuccess = noop,
  children,
  isStickyButtons = false,
  footerElement,
  buttonSize = 'standard',
}: ActionableFormViewProps) => {
  const [actionResult, setActionResult] = useState(ACTION_RESULT.NONE);
  const [errorMessage, setErrorMessage] = useState<ErrorMessageType>('');
  const { t } = useTranslations(translations);
  const handleOnSubmit = handleSubmit(async (formValues?: any) => {
    setErrorMessage('');
    setActionResult(ACTION_RESULT.LOADING);
    const result = await onSubmit(formValues);

    setActionResult(result.type);
    if (result.type === ACTION_RESULT.SUCCESS) {
      onSubmitSuccess(result.message, result.description, result.toastAction);
      if (resetForm) {
        resetForm();
      }
    } else if (typeof onSubmitError === 'function') {
      onSubmitError(result.errorMessage);
    } else {
      if (onSubmitErrorWithSetErrorMessage) {
        onSubmitErrorWithSetErrorMessage();
      }
      setErrorMessage(result.errorMessage);
    }
  });

  const handleOnCancel = () => {
    setErrorMessage('');
    setActionResult(ACTION_RESULT.NONE);
    if (typeof onCancel === 'function') {
      onCancel();
    }
  };

  const activeButtons = (
    <Actions>
      <Button
        id={`form-${submitButtonLabel}-submit`}
        type="submit"
        tone="formAccent"
        variant="solid"
        size={buttonSize}
      >
        {submitButtonLabel}
      </Button>
      {onCancel && (
        <Button
          data={{ testid: `form-${submitButtonLabel}-cancel` }}
          type="button"
          onClick={handleOnCancel}
          variant="transparent"
          tone="neutral"
          size={buttonSize}
        >
          {t('Cancel')}
        </Button>
      )}
    </Actions>
  );

  const inactiveButtons = (
    <Actions>
      <Button loading tone="formAccent" size={buttonSize}>
        {submitButtonLoadingLabel}
      </Button>
    </Actions>
  );

  return (
    <form
      onSubmit={handleOnSubmit}
      data-testid={`actionable-${submitButtonLabel}-form`}
    >
      <StickyButtonsFormView
        isStickyButtons={isStickyButtons}
        buttons={
          ACTION_RESULT.LOADING === actionResult
            ? inactiveButtons
            : activeButtons
        }
        errorMessage={errorMessage}
        footerElement={footerElement}
      >
        <fieldset
          disabled={ACTION_RESULT.LOADING === actionResult}
          className={styles.fieldset}
        >
          {children}
        </fieldset>
      </StickyButtonsFormView>
    </form>
  );
};
export default observer(ActionableFormView);
